// Global
import Head from 'next/head';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import {
  GetStaticComponentProps,
  ImageField,
  Text,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
// Components
import Component from 'src/helpers/Component/Component';
import { ProductIntroTheme } from './ProductIntro.theme';
import Headline from 'src/helpers/Headline/Headline';
import { ImageToggleWrapper } from 'src/helpers/ImageToggleWrapper';
import { BodyCopy } from 'src/helpers/BodyCopy';
import { Eyebrow } from 'src/helpers/Eyebrow';
import ButtonGroup from 'src/helpers/ButtonGroup/ButtonGroup';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import ImageWrapper from 'src/helpers/Media/ImageWrapper';
import { Subheadline } from 'src/helpers/Subheadline';
import { maxhTypes, maxwTypes } from 'src/helpers/Media';
import ImagePrimary from 'src/helpers/Media/ImagePrimary';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { ComponentProps } from 'lib/types/component-props';
import SingleButton from 'src/helpers/SingleButton/SingleButton';
import { Foundation } from 'src/.generated/Foundation.EnterpriseWeb.model';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { Button, cta1ToButtonProps, cta2ToButtonProps } from 'src/helpers/Button';
import { RichTextWrapper } from 'src/helpers/RichTextWrapper';
import PriceLevel from 'src/helpers/PriceLevel/PriceLevel';
import CallToActionAR from 'src/helpers/CallToActionAR/CallToActionAR';
import { useBVScript } from 'lib/utils/use-bv-script';
import DisclaimerText from 'src/helpers/DisclaimerText/DisclaimerText';
import { StarRating } from 'src/helpers/StarRating';
import {
  useReviewSummary,
  getReviewsSummary,
  parseFilterRuleDataForAggregates,
} from 'lib/reviews/get-reviews-v2';
import { useAffiliate } from 'lib/context/AffiliateContext';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { mapSearchResults } from 'lib/graphql/mappers/map-search-results';
import { IntegratedGraphQlResult } from 'lib/graphql/types/integrated-graphql-result';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import { ItemSearchResults } from 'lib/graphql/types/item-search-results';
export type ProductSwatch = Feature.EnterpriseWeb.Enterprise.Components.Product.ProductSwatch & {
  fields?: {
    productImageSwatch?: Feature.EnterpriseWeb.Enterprise.Elements.Swatches.Swatch;
  };
};

export type ProductIntroProps = Feature.EnterpriseWeb.Enterprise.Components.Product.ProductIntro & {
  fields?: {
    children?: ProductSwatch[];
    tabLinkToSelect: Foundation.EnterpriseWeb.Enterprise.FieldSets.ContentAnchor;
  };
} & ComponentProps;

const ProductIntro = (props: ProductIntroProps) => {
  const { fields, staticProductReview, awAggregateRating } = props;
  const { themeName, themeData } = useTheme(ProductIntroTheme);
  const productFields: any = useMemo(() => {
    if (fields?.data) {
      const mappedFields = fields.data.item.fields.map((field: any) => {
        return { [field.name]: field.jsonValue };
      });
      const result = Object.assign({}, ...mappedFields);
      const mappedChildrenFields = fields.data.item.children?.results?.map((field: any) => {
        return {
          fields: Object.assign(
            {},
            ...field?.fields?.map((innerField: any) => {
              return { [innerField.name]: innerField.jsonValue };
            })
          ),
        };
      });

      result.children = mappedChildrenFields;

      return result;
    }

    return fields;
  }, [fields?.data]);

  const propFields: any = useMemo(() => {
    if (productFields !== undefined && 'children' in productFields) {
      return { fields: productFields };
    }
    return {};
  }, [productFields]);

  const favoriteProductsArr = props.favoriteProducts || [];
  const showFavorite = productFields?.productFavorite?.value;
  const productID = productFields?.productItem?.fields?.productId?.value;
  const isFavorited = favoriteProductsArr.includes(productID);
  const [isInterior, setIsInterior] = useState<boolean>(true);
  const [selectedSwatchColor, setSelectedSwatchColor] = useState<number | undefined>();
  const [colorSwatches, setColorSwatches] = useState<ProductSwatch[] | undefined>();
  const { currentScreenWidth } = useCurrentScreenType();
  const { userAffiliate } = useAffiliate();
  const reviewModCategory = productFields?.productItem?.fields?.reviewModCategory?.map(
    (review: any) => {
      return review?.fields?.Value.value;
    }
  );
  const affiliateId =
    userAffiliate?.affiliateId && userAffiliate.affiliateId !== '0'
      ? [userAffiliate.affiliateId]
      : undefined;
  const filters = parseFilterRuleDataForAggregates(reviewModCategory, affiliateId);
  const productReview = useReviewSummary(filters);
  const { sitecoreContext } = useSitecoreContext();
  // Add the bazaarvoice script
  useBVScript({ environment: process.env.NEXT_PUBLIC_EW_ENVIRONMENT, theme: themeName });

  const bazaarvoiceProductId = productFields?.productItem?.fields?.bazaarvoiceProductId?.value;

  function updateToggleState(state: boolean) {
    setIsInterior(state);
  }

  let rbaPrimaryImage = {};
  let primaryImageVal = {};
  let primaryImageMobileVal = {};
  if (themeName === 'rba') {
    rbaPrimaryImage =
      !!productFields?.primaryImage &&
      !!productFields?.primaryImage?.value?.src &&
      !!productFields?.primaryImageMobile?.value?.src
        ? {
            image: productFields?.primaryImage,
            mobileImage: productFields?.primaryImageMobile,
            mobileFocusArea: productFields?.primaryImageMobileFocusArea,
            additionalDesktopClasses: 'p-m h-fit w-full',
            maxH: 'max-h-[543px]' as maxhTypes,
            maxW: 'max-w-[543px]' as maxwTypes,
            additionalMobileClasses: 'p-s h-full w-full max-w-[343px]',
          }
        : false;

    const primaryImage =
      productFields.primaryImage &&
      productFields.primaryImage?.value?.src &&
      productFields.primaryImage.value;
    const primaryImageMobile =
      productFields.primaryImageMobile &&
      productFields.primaryImageMobile?.value?.src &&
      productFields.primaryImageMobile?.value;
    const productImage =
      (productFields.productItem &&
        productFields.productItem?.productFields?.productImage?.value) ||
      {};
    const productImageMobile =
      (productFields.productItem &&
        productFields.productItem?.productFields?.productImageMobile?.value) ||
      {};

    primaryImageVal = primaryImage || productImage || {};
    primaryImageMobileVal = primaryImageMobile || productImageMobile || primaryImageVal;
  }

  const swatchHeading =
    themeName === 'rba'
      ? productFields?.interiorSwatchesHeadline
      : isInterior
      ? productFields?.interiorSwatchesHeadline
      : productFields?.exteriorSwatchesHeadline;

  const interiorColorSwatches = useMemo(() => {
    return productFields?.children?.filter((_childItem: ProductSwatch) => {
      return _childItem?.fields?.interior.value;
    });
  }, [productFields]);

  const exteriorColorSwatches = useMemo(() => {
    return productFields?.children?.filter((_childItem: ProductSwatch) => {
      return !_childItem?.fields?.interior.value;
    });
  }, [productFields]);

  const [tabUrl, setTabUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const tabId =
      productFields?.tabLinkToSelect?.fields?.contentId?.value ||
      props?.fields?.tabLinkToSelect?.id ||
      productFields?.tabLinkToSelect?.id;
    const newUrl = new URL(`${window.location.pathname}#${tabId}`, window.location.href);
    setTabUrl(tabId ? newUrl.href : undefined);
    // we can ignore suggested deps as they are directly coming from layout and can be ommited here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    isInterior ? setColorSwatches(interiorColorSwatches) : setColorSwatches(exteriorColorSwatches);
    if (
      themeName === 'aw' &&
      (productFields?.children?.length || fields.data?.item.children?.results.length)
    ) {
      setSelectedSwatchColor(0);
    }
  }, [
    exteriorColorSwatches,
    productFields?.children?.length,
    fields.data?.item.children?.results.length,
    interiorColorSwatches,
    isInterior,
    themeName,
  ]);

  if (!productFields) {
    return null;
  }

  const averageRating = productReview?.averageRating || '';
  const totalReviews = productReview?.totalReviews || '';
  const totalProjectCount = productReview?.totalProjects || '';
  const priceLevel = parseInt(
    (
      (
        productFields.productItem as Foundation.EnterpriseWeb.Enterprise.FieldSets.Products.PriceLevelField
      )?.fields?.priceLevel as unknown as Feature.EnterpriseWeb.Enterprise.Data.Products.PriceLevel
    )?.fields?.priceLevelText?.value || ''
  );

  function renderColorSwatches(): JSX.Element {
    if (colorSwatches?.length) {
      return (
        <>
          <Subheadline
            useTag="div"
            classes={themeData.classes.swatchHeadline}
            fields={{
              subheadlineText: swatchHeading || { value: '' },
            }}
          />

          <ul className="mb-m flex gap-m">
            {colorSwatches?.map((_childItem, index: number) => {
              return (
                <li key={`colorswatch-${index}`} className="flex flex-col items-center">
                  <span
                    tabIndex={themeName === 'aw' ? 0 : -1}
                    onClick={() => {
                      if (themeName === 'aw') {
                        setSelectedSwatchColor(index);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (themeName === 'aw' && (e.code === 'Enter' || e.code === 'Space')) {
                        setSelectedSwatchColor(index);
                      }
                    }}
                    className={classNames(
                      'block h-[46px] w-[46px]  rounded-full p-[2px]',
                      selectedSwatchColor === index && themeName === 'aw' ? 'border-2' : '',
                      themeName === 'aw' ? 'cursor-pointer' : ''
                    )}
                  >
                    <span className="mx-auto block h-[38px] w-[38px] rounded-full  [&_img]:rounded-full">
                      <ImagePrimary
                        imageLayout="responsive"
                        fields={{
                          primaryImage: _childItem?.fields?.productImageSwatch?.fields
                            ?.swatchImage as ImageField,
                          primaryImageMobile: _childItem?.fields?.productImageSwatch?.fields
                            ?.swatchImage as ImageField,
                          primaryImageCaption: {
                            value: '',
                          },
                        }}
                      />
                    </span>
                  </span>

                  <Subheadline
                    useTag="span"
                    fields={{
                      subheadlineText: _childItem?.fields?.productImageSwatch?.fields
                        ?.swatchName || {
                        value: '',
                      },
                    }}
                    classes={themeData.classes.swatchTitle}
                  />
                </li>
              );
            })}
          </ul>
        </>
      );
    } else {
      return <></>;
    }
  }

  let aggregateRating;

  if (themeName === 'rba' && staticProductReview?.averageRating) {
    aggregateRating = {
      '@type': 'AggregateRating',
      reviewCount: staticProductReview.totalReviews,
      ratingValue: staticProductReview.averageRating,
    };
  }
  if (themeName === 'aw') {
    aggregateRating = {
      '@type': 'AggregateRating',
      reviewCount: awAggregateRating?.ReviewStatistics?.TotalReviewCount,
      ratingValue: awAggregateRating?.ReviewStatistics?.OverallRatingRange,
    };
  }

  const brandName =
    themeName === 'aw' ? 'Andersen Windows' : themeName === 'rba' ? 'Renewal by Andersen' : '';

  const ldJsonScript = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productFields?.productItem?.fields?.productName?.value || '',
    description: productFields?.productItem?.fields?.productDescription?.value || '',
    brand: {
      '@type': 'Brand',
      name: brandName,
    },
    image: productFields?.productItem?.fields?.productImage?.value?.src || '',
    url: `${sitecoreContext?.extendedSiteInfo?.canonicalHostName}${productFields?.productItem?.fields?.productDetailPageLink?.value?.href}`,
    ...(aggregateRating && { aggregateRating }),
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJsonScript) }}
        />
      </Head>
      <Component variant="lg" dataComponent="product/productintro" {...props}>
        {/* Favourite */}
        {showFavorite && (
          <div className="absolute -top-[16px] right-0 max-md:hidden">
            <div
              className={classNames(
                themeData.classes.favoriteProduct,
                isFavorited ? 'favorited border-[transparent_#f26924_transparent_transparent]' : ''
              )}
              data-product-id={productID}
            >
              <SvgIcon
                icon="favorite"
                fillId="white"
                size="xl"
                className={themeData.classes.favoriteIcon}
              />
            </div>
          </div>
        )}
        <div className={themeData.classes.imageColClasses}>
          {themeName === 'aw' ? (
            <ImageToggleWrapper
              fields={{
                primaryImage: productFields.primaryImage,
                secondaryImage: productFields.secondaryImage,
                primaryImageMobile: productFields?.primaryImageMobile,
                secondaryImageMobile: productFields?.secondaryImageMobile,
                primaryImageMobileFocusArea: productFields?.primaryImageMobileFocusArea,
                secondaryImageMobileFocusArea: productFields?.secondaryImageMobileFocusArea,
              }}
              colorSwatches={{
                interiorColorSwatches: interiorColorSwatches,
                exteriorColorSwatches: exteriorColorSwatches,
              }}
              selectedSwatchIndex={selectedSwatchColor}
              updateToggleState={updateToggleState}
              ratio={'square'}
            />
          ) : (
            <>
              {rbaPrimaryImage ? (
                <ImageWrapper {...rbaPrimaryImage} imageLayout="responsive" ratio="square" />
              ) : primaryImageVal || primaryImageMobileVal ? (
                <ImagePrimary
                  additionalDesktopClasses="p-m h-fit w-fit"
                  additionalMobileClasses="p-s h-full w-fit max-w-[343px]"
                  maxH="max-h-[543px]"
                  maxW="max-w-[543px]"
                  imageLayout="intrinsic"
                  ratio="square"
                  fields={{
                    primaryImageCaption: {
                      value: '',
                    },
                    primaryImage: { value: primaryImageVal },
                    primaryImageMobile: { value: primaryImageMobileVal },
                    primaryImageMobileFocusArea:
                      productFields.productItem?.fields?.primaryImageMobileFocusArea &&
                      productFields.productItem?.fields?.primaryImageMobileFocusArea?.targetItem
                        ?.value.value
                        ? {
                            id: '',
                            url: '',
                            name: productFields.productItem?.fields.primaryImageMobileFocusArea
                              .targetItem.value.value,
                            displayName:
                              productFields.productItem?.fields.primaryImageMobileFocusArea
                                .targetItem.value.value,
                            fields: {
                              Value: {
                                value:
                                  productFields.productItem?.fields.primaryImageMobileFocusArea
                                    .targetItem.value.value,
                              },
                            },
                          }
                        : {
                            id: '',
                            url: '',
                            name: 'Center',
                            displayName: 'Center',
                            fields: {
                              Value: {
                                value: 'center',
                              },
                            },
                            templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                            templateName: 'Enum',
                          },
                  }}
                />
              ) : (
                <></>
              )}
              {/* RBA Desktop custom CTA for tab switch */}
              {currentScreenWidth > getBreakpoint('md') && tabUrl && (
                <SingleButton
                  fields={{
                    cta1Icon: {
                      id: 'f8ad4587-51a4-4e66-8eec-b448f78b4cb2',
                      name: 'Augmented Reality',
                      displayName: 'Augmented Reality',
                      fields: {
                        Value: {
                          value: 'arrow',
                        },
                      },
                      templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                      templateName: 'Enum',
                      url: '',
                    },
                    cta1Link: {
                      value: {
                        href: tabUrl,
                        text: productFields?.tabLinkText?.value,
                        anchor: '',
                        linktype: 'internal',
                        class: '',
                        title: '',
                        target: '',
                        querystring: '',
                        id: '{BD66C47E-42B0-4EDD-BAD3-4BC981C05E5D}',
                      },
                    },
                    cta1Style: {
                      id: '8aedd89c-e161-41d4-b773-6a6097a19372',
                      name: 'Secondary',
                      displayName: 'Secondary',
                      fields: {
                        Value: {
                          value: 'secondary',
                        },
                      },
                      templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                      templateName: 'Enum',
                      url: '',
                    },
                    cta1ModalLinkText: {
                      value: '',
                    },
                    cta1AriaLabel: {
                      value: '',
                    },
                  }}
                />
              )}
            </>
          )}
        </div>
        <div className={themeData.classes.descriptionColClasses}>
          {currentScreenWidth < getBreakpoint('md') && themeName === 'rba' && (
            <>
              <div
                className={classNames(
                  themeData.classes.buttonGroupClass?.wrapper,
                  'mb-s flex items-start space-y-s md:flex-row md:items-center md:space-x-4 md:space-y-0'
                )}
              >
                {productFields?.cta1Link && (
                  <Button
                    field={productFields?.cta1Link}
                    variant={productFields?.cta1Style}
                    icon={productFields?.cta1Icon}
                    modalId={
                      (
                        productFields?.cta1Modal as unknown as Feature.EnterpriseWeb.Enterprise.Components.Modal.GenericModal.GenericModal
                      )?.fields?.modalId?.value
                    }
                    modalLinkText={productFields?.cta1ModalLinkText}
                    classes={classNames(themeData.classes.buttonGroupClass?.cta1Classes, 'mr-m')}
                  />
                )}

                {currentScreenWidth < getBreakpoint('md') && tabUrl && (
                  <SingleButton
                    classes={{ wrapper: 'w-full', cta1Classes: '!w-full md:!w-fit justify-center' }}
                    fields={{
                      cta1Icon: {
                        id: 'f8ad4587-51a4-4e66-8eec-b448f78b4cb2',
                        name: 'Augmented Reality',
                        displayName: 'Augmented Reality',
                        fields: {
                          Value: {
                            value: 'arrow',
                          },
                        },
                        templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                        templateName: 'Enum',
                        url: '',
                      },
                      cta1Link: {
                        value: {
                          href: tabUrl,
                          text: productFields?.tabLinkText?.value,
                          anchor: '',
                          linktype: 'internal',
                          class: '',
                          title: '',
                          target: '',
                          querystring: '',
                          id: '{BD66C47E-42B0-4EDD-BAD3-4BC981C05E5D}',
                        },
                      },
                      cta1Style: {
                        id: '8aedd89c-e161-41d4-b773-6a6097a19372',
                        name: 'Secondary',
                        displayName: 'Secondary',
                        fields: {
                          Value: {
                            value: 'secondary',
                          },
                        },
                        templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                        templateName: 'Enum',
                        url: '',
                      },
                      cta1ModalLinkText: {
                        value: '',
                      },
                      cta1AriaLabel: {
                        value: '',
                      },
                    }}
                  />
                )}

                {productFields?.cta2Link && (
                  <Button
                    field={productFields?.cta2Link}
                    variant={productFields?.cta2Style}
                    icon={productFields?.cta2Icon}
                    modalId={
                      (
                        productFields?.cta2Modal as unknown as Feature.EnterpriseWeb.Enterprise.Components.Modal.GenericModal.GenericModal
                      )?.fields?.modalId?.value
                    }
                    modalLinkText={productFields?.cta2ModalLinkText}
                    classes={themeData.classes.buttonGroupClass?.cta2Classes}
                  />
                )}
              </div>
            </>
          )}
          {/* Favourite */}
          {showFavorite && (
            <div className={classNames(themeData.classes.favoriteProductWrapper)}>
              <div
                className={classNames(
                  themeData.classes.favoriteProduct,
                  isFavorited
                    ? 'favorited border-[transparent_#f26924_transparent_transparent]'
                    : ''
                )}
                data-product-id={productID}
              >
                <SvgIcon
                  icon="favorite"
                  fillId="white"
                  size="xl"
                  className={themeData.classes.favoriteIcon}
                />
              </div>
            </div>
          )}
          {productFields.eyebrowText?.value ? (
            <Eyebrow
              classes={themeData.classes.eyebrow}
              {...(!props?.fields?.data ? props : propFields)}
            />
          ) : productFields.productItem?.fields?.productSeries &&
            productFields.productItem?.fields?.productSeries?.fields?.productTypeName?.value ? (
            <Text
              tag="h4"
              className={themeData.classes.eyebrow}
              field={{
                value:
                  productFields.productItem?.fields?.productSeries?.fields?.productTypeName?.value,
              }}
            />
          ) : (
            <></>
          )}

          {productFields.headlineText?.value ? (
            <Headline
              classes={themeData.classes.headline}
              {...(!props?.fields?.data ? props : propFields)}
            />
          ) : productFields.productItem?.fields?.productName ? (
            <div className={themeData.classes.headlineWrapper}>
              <Text
                useTag="h4"
                className={themeData.classes.headline}
                field={{ value: productFields.productItem.fields.productName.value }}
              />
            </div>
          ) : (
            <></>
          )}

          {productFields.body?.value ? (
            <BodyCopy
              classes={classNames(themeData.classes.bodyClass, 'overflow-x-auto')}
              {...(!props?.fields?.data ? props : propFields)}
            />
          ) : productFields.productItem?.fields?.productDescription ? (
            <RichTextWrapper
              field={{ value: productFields.productItem.fields.productDescription.value }}
              classes={themeData.classes.bodyClass}
            />
          ) : (
            <></>
          )}
          {productFields?.claim?.value ? (
            <RichTextWrapper
              field={{ value: productFields.claim?.value }}
              classes={themeData.classes.claimClass}
            />
          ) : (
            <></>
          )}
          {productFields?.disclaimer?.value ? (
            <DisclaimerText
              fields={productFields?.disclaimer}
              disclaimerLayoutClasses="col-span-12 md:col-span-10 md:col-start-2"
              disclaimerClasses="!text-dark-gray mb-s"
            />
          ) : (
            <></>
          )}

          {themeName === 'rba' && renderColorSwatches()}
          {/* ratings */}
          <div className={themeData.classes.ratingsAndPriceWrapper}>
            <div className={themeData.classes.ratingsWrapper}>
              {bazaarvoiceProductId && themeName === 'aw' && (
                <div
                  className="review flex-[0_0_auto]"
                  data-bv-show="rating_summary"
                  data-bv-product-id={bazaarvoiceProductId}
                ></div>
              )}
              {themeName === 'rba' && averageRating && (
                <>
                  {/* Ratings icons RbA */}
                  <div className={themeData.classes.ratingsIconsList}>
                    <StarRating reviewStars={averageRating} />
                  </div>

                  {/* Ratings text RbA */}
                  <div className={themeData.classes.totalReviewsText}>
                    <Text tag="h5" field={{ value: totalReviews.toLocaleString() }} />
                    <h5 className={themeData.classes.ratingText}>Ratings</h5>
                  </div>
                </>
              )}
            </div>
            {/* Total Projects RbA */}
            {themeName === 'rba' && totalProjectCount && (
              <>
                <div className={themeData.classes.totalProjectWrapper}>
                  <Text
                    tag="h5"
                    className={themeData.classes.totalProjectCount}
                    field={{ value: totalProjectCount.toLocaleString() }}
                  />
                  <h5 className={themeData.classes.totalProjectText}>
                    Projects completed in your area
                  </h5>
                </div>
              </>
            )}
            {/* Price Level */}
            {priceLevel ? (
              <div
                className={
                  bazaarvoiceProductId
                    ? themeData.classes.priceLevelWrapper
                    : themeName === 'rba'
                    ? themeData.classes.priceLevelWrapper
                    : ''
                }
              >
                <PriceLevel
                  priceLevel={priceLevel}
                  priceClasses={themeData.classes.priceTextClasses}
                  priceLevelClasses={themeData.classes.priceLevelClasses}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
          {themeName === 'aw' ? (
            <div className="flex-none md:flex md:flex-wrap">
              <ButtonGroup
                cta1={cta1ToButtonProps(
                  !props?.fields?.data ? props : propFields,
                  themeData.classes.buttonGroupClass.cta1Classes
                )}
                cta2={cta2ToButtonProps(
                  !props?.fields?.data ? props : propFields,
                  themeData.classes.buttonGroupClass.cta2Classes
                )}
                wrapperClasses={themeData.classes.buttonGroupClass.wrapper}
              />
              {productFields?.AR?.fields && (
                <CallToActionAR
                  classes={{
                    wrapper: '',
                    buttonClasses:
                      'my-s flex w-fit items-center whitespace-nowrap rounded-lg border-4 border-black px-m py-[9px] font-sans text-button font-heavy hover:bg-black hover:text-white disabled:border-gray disabled:text-gray md:my-0',
                  }}
                  {...productFields?.AR}
                />
              )}
            </div>
          ) : (
            <>
              {currentScreenWidth > getBreakpoint('md') && (
                <>
                  {currentScreenWidth < getBreakpoint('md') && tabUrl && (
                    <SingleButton
                      fields={{
                        cta1Icon: {
                          id: 'f8ad4587-51a4-4e66-8eec-b448f78b4cb2',
                          name: 'Augmented Reality',
                          displayName: 'Augmented Reality',
                          fields: {
                            Value: {
                              value: 'arrow',
                            },
                          },
                          templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                          templateName: 'Enum',
                          url: '',
                        },
                        cta1Link: {
                          value: {
                            href: tabUrl,
                            text: productFields?.tabLinkText?.value,
                            anchor: '',
                            linktype: 'internal',
                            class: '',
                            title: '',
                            target: '',
                            querystring: '',
                            id: '{BD66C47E-42B0-4EDD-BAD3-4BC981C05E5D}',
                          },
                        },
                        cta1Style: {
                          id: '8aedd89c-e161-41d4-b773-6a6097a19372',
                          name: 'Secondary',
                          displayName: 'Secondary',
                          fields: {
                            Value: {
                              value: 'secondary',
                            },
                          },
                          templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                          templateName: 'Enum',
                          url: '',
                        },
                        cta1ModalLinkText: {
                          value: '',
                        },
                        cta1AriaLabel: {
                          value: '',
                        },
                      }}
                    />
                  )}
                  <ButtonGroup
                    cta1={cta1ToButtonProps(
                      !props?.fields?.data ? props : propFields,
                      themeData.classes.buttonGroupClass.cta1Classes
                    )}
                    cta2={cta2ToButtonProps(
                      !props?.fields?.data ? props : propFields,
                      themeData.classes.buttonGroupClass.cta2Classes
                    )}
                    wrapperClasses={themeData.classes.buttonGroupClass.wrapper}
                  />
                </>
              )}
            </>
          )}

          {themeName === 'aw' && (
            <>
              {renderColorSwatches()}
              {/* AW custom cta for tab switch */}
              {tabUrl && (
                <SingleButton
                  fields={{
                    cta1Icon: {
                      id: 'f8ad4587-51a4-4e66-8eec-b448f78b4cb2',
                      name: 'Augmented Reality',
                      displayName: 'Augmented Reality',
                      fields: {
                        Value: {
                          value: 'arrow',
                        },
                      },
                      templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                      templateName: 'Enum',
                      url: '',
                    },
                    cta1Link: {
                      value: {
                        href: tabUrl,
                        text: productFields?.tabLinkText?.value,
                        anchor: '',
                        linktype: 'internal',
                        class: '',
                        title: '',
                        target: '',
                        querystring: '',
                        id: '{BD66C47E-42B0-4EDD-BAD3-4BC981C05E5D}',
                      },
                    },
                    cta1Style: {
                      id: '8aedd89c-e161-41d4-b773-6a6097a19372',
                      name: 'Secondary',
                      displayName: 'Secondary',
                      fields: {
                        Value: {
                          value: 'tertiary',
                        },
                      },
                      templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                      templateName: 'Enum',
                      url: '',
                    },
                    cta1ModalLinkText: {
                      value: '',
                    },
                    cta1AriaLabel: {
                      value: '',
                    },
                  }}
                />
              )}
            </>
          )}
        </div>
      </Component>
    </>
  );
};
type IntegratedGraphQl = IntegratedGraphQlResult<{
  item: {
    fields: ItemFieldResult[];
    children: ItemSearchResults<{
      fields: ItemFieldResult[];
    }>;
  };
}>;
export default withDatasourceCheck()<ProductIntroProps>(ProductIntro);
export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  let result: any = {};
  const productReviewStaticProps: { staticProductReview?: any; awAggregateRating?: any } = {};

  // First part
  if (rendering.fields !== undefined && 'data' in rendering.fields) {
    const fields = rendering.fields as unknown as IntegratedGraphQl;

    result = {
      fields: {
        ...mapItemFieldResultsToObject(fields.data.item.fields),
        children: mapSearchResults(fields.data.item.children, (child) => ({
          fields: {
            ...mapItemFieldResultsToObject(child.fields),
          },
        })),
      },
    };
  }

  // Second part
  const datasource = rendering as ProductIntroProps;

  if (datasource) {
    const productItem = datasource?.fields?.data?.item?.fields?.find(
      (field: any) => field.name === 'productItem'
    )?.jsonValue;
    if (!productItem) {
      console.warn('No productItem found in datasource.');
      return { ...result };
    }
    let reviewModCategory: string[] = [];
    const rm = productItem?.fields?.reviewModCategory;
    if (Array.isArray(rm)) {
      reviewModCategory = rm.map((review: any) => review?.fields?.Value?.value).filter(Boolean);
    } else if (rm?.value) {
      reviewModCategory = [rm.value];
    }

    const awAggregateRating = productItem?.fields?.bazaarvoiceProductId?.value?.trim();

    if (reviewModCategory.length > 0) {
      const filters = parseFilterRuleDataForAggregates(reviewModCategory);
      const productReview = await getReviewsSummary(filters);
      productReviewStaticProps.staticProductReview = productReview;
    }
    if (awAggregateRating) {
      try {
        const sanitizeProductId = awAggregateRating.replace(/\s/g, '');
        const apiKey = process.env.BAZAARVOICE_API_KEY;
        const baseUrl = process.env.BAZAARVOICE_API_URL;

        const apiUrl = `${baseUrl}${apiKey}&Filter=ProductId:${sanitizeProductId}&Include=Products,Comments&Stats=Reviews`;
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Bazaarvoice API error: ${response.status}`);
        }
        const directReview = await response.json();
        const ratingData = directReview?.Includes?.Products?.[sanitizeProductId];
        productReviewStaticProps.awAggregateRating = ratingData !== undefined ? ratingData : null;
      } catch (error) {
        console.error('Error fetching direct Bazaarvoice review:', error);
        productReviewStaticProps.awAggregateRating = null;
      }
    } else {
      console.warn('No valid productId found.');
      productReviewStaticProps.awAggregateRating = null;
    }
  }
  return {
    ...result,
    ...productReviewStaticProps,
  };
};
